import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Graphic from "@arcgis/core/Graphic";
import Polygon from "@arcgis/core/geometry/Polygon";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import * as projection from "@arcgis/core/geometry/projection";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { scaleForZoom } from "../composables/ZoomLevels";
import { GREY, greyFill, labelSymbol, linkedSymbol } from "./LPISLayerBuilder";

// DAFM anonymous LPIS for the farm map, served by this app from the
// GeoPackage on the server (App\Helpers\Lpis\DafmLpis). Parcels arrive in
// Irish Transverse Mercator metres and are projected here to the map's
// reference system.
//
// Two client-side FeatureLayers, like the ISEC layers:
// - context: every parcel in view, loaded in 2 km cells, only when zoomed in;
// - linked: the farmer's linked parcels, drawn at every zoom.

const ITM = { wkid: 2157 };

// A full screen at zoom 15 covers a few thousand parcels
export const DAFM_CONTEXT_MIN_SCALE = scaleForZoom(15);

const MAX_LOADED_PARCELS = 30000;
const MAX_CELLS_PER_VIEW = 80;
const PARALLEL_REQUESTS = 6;

const FIELDS = [
  { name: "oid", type: "oid" },
  { name: "id", type: "integer" },
  { name: "app_herd", type: "string" },
  { name: "claim_area", type: "double" },
  { name: "commonage_ind", type: "string" },
  { name: "crop", type: "string" },
  { name: "olr", type: "string" },
  { name: "area_ha", type: "double" },
];

// An invisible fill rather than none, so clicks inside an outline hit the parcel
const CLEAR = [0, 0, 0, 0];

// Claimed parcels grey; commonage is stacked once per shareholder, so it is an
// outline only; unclaimed parcels get a thin outline.
const contextRenderer = {
  type: "unique-value",
  valueExpression:
    "When(IsEmpty($feature.app_herd), 'unclaimed', $feature.commonage_ind == 'Y', 'commonage', 'claimed')",
  defaultSymbol: greyFill,
  uniqueValueInfos: [
    { value: "claimed", symbol: greyFill },
    {
      value: "commonage",
      symbol: { type: "simple-fill", color: CLEAR, outline: { color: [255, 255, 0, 0.9], width: 1.5 } },
    },
    {
      value: "unclaimed",
      symbol: { type: "simple-fill", color: CLEAR, outline: { color: GREY, width: 0.5 } },
    },
  ],
};

// The farmer's parcels in pink; commonage keeps a yellow edge
const linkedRenderer = {
  type: "unique-value",
  field: "commonage_ind",
  defaultSymbol: linkedSymbol,
  uniqueValueInfos: [
    { value: "Y", symbol: { ...linkedSymbol, outline: { color: [255, 255, 0], width: 2 } } },
  ],
};

// The farm map is Web Mercator (imagery basemap); see ArcgisFeature.js
const MAP_SR = SpatialReference.WebMercator;

const LAYER_OPTIONS = {
  source: [],
  fields: FIELDS,
  objectIdField: "oid",
  geometryType: "polygon",
  spatialReference: MAP_SR,
  copyright: "LPIS: Department of Agriculture, Food and the Marine, CC BY 4.0",
};

export default class DafmLpisSource {
  // cellSize: side of a map cell in metres, LpisController::CELL_SIZE.
  // version(): the server file's version, read on each request so it stays
  // current; it goes on cell and holding URLs so browsers can cache them.
  constructor(cellSize, version = () => null) {
    this.cellSize = cellSize;
    this.version = version;
    // Layer replacements, run one at a time (see replaceFeatures)
    this.replacing = Promise.resolve();
    this.minScale = DAFM_CONTEXT_MIN_SCALE;
    this.unavailable = false;
    this.loadedIds = new Set();
    this.loadedCells = new Set();
    // Changes when the grey layer is cleared; cell loads from before drop out
    this.generation = 0;
    this.watchHandle = null;
    this.view = null;
    this.ready = projection.load();
  }

  getLayers() {
    if (this.layers) return this.layers;

    const context = new FeatureLayer({
      ...LAYER_OPTIONS,
      title: "LPIS",
      // Hidden until the layer toggle or linking shows it
      visible: false,
      minScale: this.minScale,
      maxScale: 0,
      renderer: contextRenderer,
      labelingInfo: [
        {
          labelExpressionInfo: {
            // Parcel size, measured from the shape; unattributed parcels have no other area
            expression: "Text($feature.area_ha, '#,##0.00') + ' ha'",
          },
          maxScale: 0,
          minScale: 50000,
          symbol: labelSymbol,
        },
      ],
    });

    const linked = new FeatureLayer({
      ...LAYER_OPTIONS,
      title: "Farmer LPIS",
      renderer: linkedRenderer,
      legendEnabled: false,
    });

    this.layers = { context, linked };
    return this.layers;
  }

  // ---- Server requests ----

  // Commonage rows after the first that share its shape carry same_shape_as
  // instead of rings and area; they get a copy of the first row's.
  async toGraphics(features) {
    await this.ready;
    const byId = new Map();
    return features.map(({ rings, same_shape_as: sameAs, ...attributes }) => {
      const first = sameAs ? byId.get(sameAs) : null;
      let geometry;
      if (first) {
        geometry = first.geometry.clone();
        attributes.area_ha = first.attributes.area_ha;
      } else {
        geometry = projection.project(new Polygon({ rings: rings || [], spatialReference: ITM }), MAP_SR);
      }
      const graphic = new Graphic({ geometry, attributes });
      byId.set(attributes.id, graphic);
      return graphic;
    });
  }

  // Route parameters plus the file version, when known
  withVersion(params) {
    const v = this.version();
    return v ? { ...params, v } : params;
  }

  async get(url) {
    try {
      const response = await axios.get(url);
      return this.toGraphics(response.data.features || []);
    } catch (error) {
      if (error.response?.status === 503) {
        this.unavailable = true;
        console.warn("DAFM LPIS data is not available on this server.");
        return [];
      }
      throw error;
    }
  }

  // Every row under a map point, commonage stacked once per shareholder.
  async queryAtPoint(mapPoint) {
    await this.ready;
    const point = projection.project(mapPoint, ITM);
    return this.get(route("app.lpis.dafm.at", { x: point.x.toFixed(1), y: point.y.toFixed(1) }));
  }

  queryHolding(herd) {
    return this.get(route("app.lpis.dafm.holding", this.withVersion({ herd })));
  }

  fetchLinked(farmerHashid) {
    return this.get(route("app.farmer.lpis-parcels.show", farmerHashid));
  }

  // ---- Layers ----

  // Replacements run one after another, so two overlapping ones can't both
  // delete the same old features and both add theirs. A failed one doesn't
  // block the next.
  replaceFeatures(layer, graphics) {
    this.replacing = this.replacing
      .catch(() => {})
      .then(async () => {
        const existing = await layer.queryObjectIds();
        await layer.applyEdits({
          deleteFeatures: existing.map((objectId) => ({ objectId })),
          addFeatures: graphics.map((graphic) => graphic.clone()),
        });
      });
    return this.replacing;
  }

  // Load the parcels in view whenever the map stops moving at farm zoom
  watchView(view) {
    this.unwatch();
    this.view = view;
    this.watchHandle = reactiveUtils.when(
      () => view.stationary,
      () => this.loadVisible().catch((error) => console.error("Error loading LPIS parcels: ", error)),
      { initial: true }
    );
  }

  unwatch() {
    this.watchHandle?.remove();
    this.watchHandle = null;
    this.view = null;
  }

  // Shows or hides the grey parcels; hidden ones aren't loaded
  // load: false leaves loading to the next stop of the map, when it is about to move
  setContextVisible(visible, { load = true } = {}) {
    const { context } = this.getLayers();
    if (context.visible === visible) return;
    context.visible = visible;
    if (visible && load) this.loadVisible().catch((error) => console.error("Error loading LPIS parcels: ", error));
  }

  async loadVisible() {
    const view = this.view;
    if (!view || this.unavailable || view.scale > this.minScale || !this.getLayers().context.visible) return;

    await this.ready;
    // Past the cap, start afresh before listing cells, so the cells in view
    // are loaded again too
    if (this.loadedIds.size > MAX_LOADED_PARCELS) {
      await this.clearContext();
    }

    const extent = projection.project(view.extent, ITM);
    const size = this.cellSize;
    const cells = [];
    for (let x = Math.floor(extent.xmin / size); x <= Math.floor(extent.xmax / size); x++) {
      for (let y = Math.floor(extent.ymin / size); y <= Math.floor(extent.ymax / size); y++) {
        if (!this.loadedCells.has(`${x}:${y}`)) cells.push([x, y]);
      }
    }
    if (!cells.length || cells.length > MAX_CELLS_PER_VIEW) return;

    for (let i = 0; i < cells.length; i += PARALLEL_REQUESTS) {
      await Promise.all(cells.slice(i, i + PARALLEL_REQUESTS).map((cell) => this.loadCell(cell)));
    }
  }

  async loadCell([x, y]) {
    const key = `${x}:${y}`;
    const generation = this.generation;
    this.loadedCells.add(key);
    try {
      const graphics = await this.get(route("app.lpis.dafm.cell", this.withVersion({ x, y })));
      // A clear may be under way; wait for it, and drop this load if it
      // belonged to the layer as it was before
      await this.replacing.catch(() => {});
      if (generation !== this.generation) return;
      const fresh = graphics.filter((graphic) => !this.loadedIds.has(graphic.attributes.id));
      fresh.forEach((graphic) => this.loadedIds.add(graphic.attributes.id));
      if (fresh.length) {
        await this.getLayers().context.applyEdits({ addFeatures: fresh });
      }
    } catch (error) {
      this.loadedCells.delete(key);
      console.error("Error loading LPIS cell: ", error);
    }
  }

  async clearContext() {
    this.generation += 1;
    this.loadedIds.clear();
    this.loadedCells.clear();
    await this.replaceFeatures(this.getLayers().context, []);
  }

  // ---- Helpers used by the LPIS component ----

  // Parcel size in hectares, measured from the shape on the server. The
  // claimed area (claim_area) can be smaller, for example when a road or
  // scrub isn't eligible.
  areaOf(feature) {
    return Number(feature.attributes?.area_ha) || 0;
  }
}
